import classNames from 'classnames';
import * as React from 'react';
import { useController } from 'react-hook-form';
import Select, { NamedProps, OptionTypeBase, components } from 'react-select';

import api from 'api';
import FieldError from 'components/Form/FieldError';
import { useCustomFormState } from 'components/Form/Form';
import { useQuery } from 'hooks/query';

const getOptionLabel = (territory: OptionTypeBase) => territory.name;
const getOptionValue = (territory: OptionTypeBase) => String(territory.id);

interface ReactSelectProps<OptionType extends OptionTypeBase, isMulti extends boolean = false>
  extends Omit<
      React.DetailedHTMLProps<React.SelectHTMLAttributes<HTMLSelectElement>, HTMLSelectElement>,
      keyof NamedProps<OptionType, isMulti>
    >,
    NamedProps<OptionType, isMulti> {
  ref: React.Ref<any>;
}

interface SelectTerritoryMultiProps<isMulti extends boolean = false> {
  name: string;
  label?: string;
  placeholder?: string;
  helpText?: React.ReactNode;
  disabled?: boolean;
  className?: string;
  isMulti?: isMulti;
  selectProps?: any;
  elStyle?: 'fill';
  small?: boolean;
}

const DropdownIndicator = (props: any) => {
  return (
    <components.DropdownIndicator {...props}>
      <svg xmlns="http://www.w3.org/2000/svg" width="10" viewBox="0 0 24 13.71">
        <path
          d="M12 9.58L21.07.5a1.74 1.74 0 012.43 0 1.72 1.72 0 010 2.43L13.21 13.22a1.73 1.73 0 01-2.42 0L.5 2.93A1.72 1.72 0 01.5.5a1.74 1.74 0 012.43 0z"
          fillRule="evenodd"
          fill="#4c4c4c"
        />
      </svg>
    </components.DropdownIndicator>
  );
};

export const SelectTerritoryMulti = <isMulti extends boolean>({
  name,
  label,
  placeholder,
  helpText,
  disabled,
  className,
  selectProps,
  elStyle,
  small,
  ...props
}: SelectTerritoryMultiProps<isMulti>) => {
  const { hasFloatingLabels, isFiltersForm } = useCustomFormState();
  const { field, fieldState } = useController({ name });
  const [inputValue, setInputValue] = React.useState('');
  const [territoriesRes, queryState] = useQuery(api.territory.listTerritories, undefined);

  // Extract territories from the response
  const territories = React.useMemo(() => {
    if (!territoriesRes || typeof territoriesRes !== 'object' || !('data' in territoriesRes)) {
      return [];
    }
    return Array.isArray(territoriesRes.data.data) ? territoriesRes.data.data : [];
  }, [territoriesRes]);

  // Convert territories to options format
  const options = React.useMemo(() => {
    const territoryOptions = territories.map((territory) => ({
      id: territory.id,
      name: territory.region
        ? `${territory.region.region_name} > ${territory.territory_name}`
        : territory.territory_name,
      data: territory,
    }));

    return territoryOptions;
  }, [territories]);

  const wrapRef = React.useRef<HTMLDivElement>(null);

  const inputProps: ReactSelectProps<OptionTypeBase, isMulti> = {
    ...selectProps,
    value: field.value || null,
    inputValue,
    isDisabled: disabled,
    isLoading: queryState.isFetching,
    placeholder: placeholder || label,
    getOptionLabel,
    getOptionValue,
    onChange: React.useCallback<Exclude<ReactSelectProps<OptionTypeBase, isMulti>['onChange'], undefined>>(
      (value, action) => {
        if (selectProps && selectProps.onChange) {
          selectProps.onChange(value, action);
        }

        field.onChange(value);
      },
      [selectProps, field]
    ),
    onBlur: React.useCallback<Exclude<NamedProps<OptionTypeBase, isMulti>['onBlur'], undefined>>(() => {
      field.onBlur();
      if (!wrapRef.current) return;

      wrapRef.current.classList.remove('fl-has-focus');
    }, [wrapRef, field]),
    onFocus: React.useCallback<Exclude<NamedProps<OptionTypeBase, isMulti>['onFocus'], undefined>>(() => {
      if (!wrapRef.current) return;

      wrapRef.current.classList.add('fl-has-focus');
    }, [wrapRef]),
    onInputChange: React.useCallback<Exclude<NamedProps<OptionTypeBase, isMulti>['onInputChange'], undefined>>(
      (value) => {
        setInputValue(value);
      },
      []
    ),
    options,
    styles: {
      control: (base: any) => ({
        ...base,
        border: '1px solid rgba( 0, 0, 0, .05 )',
        boxShadow: '0 1px .1875rem rgba(0, 0, 0, .05)',
        backgroundColor: '#fafafa',
        ':hover': {
          borderColor: 'rgba(41, 127, 203, .2)',
        },
      }),
      indicatorSeparator: () => ({
        display: 'none',
      }),
      indicatorsContainer: (props: any) => ({
        ...props,
        marginRight: '.5rem',
      }),
      valueContainer: (base: any, state: { isMulti?: boolean; hasValue?: boolean }) => ({
        ...base,
        paddingLeft: '.875rem',
        paddingTop: state.isMulti ? (state.hasValue ? 22 : 6) : 0,
        minHeight: 48,
        paddingBottom: 6,
      }),
      singleValue: (props: any) => ({
        ...props,
        top: small ? '0.6rem' : '1.22917rem',
        transform: 'none',
        fontSize: small ? '.875rem' : '1rem',
        fontWeight: small ? '400' : '600',
        lineHeight: '1.25rem',
      }),
      multiValue: (props: any) => ({
        ...props,
        backgroundColor: '#333',
      }),
      multiValueLabel: (props: any) => ({
        ...props,
        color: '#fff',
      }),
      multiValueRemove: (props: any) => {
        return {
          ...props,
          color: '#999',
          ':hover': { ...props[':hover'], backgroundColor: undefined, color: '#e55446' },
        };
      },
      input: (base: any) => ({
        ...base,
        paddingTop: 0,
        paddingBottom: 0,
        marginTop: 0,
        marginBottom: 0,
        input: {
          minHeight: 'auto !important',
          paddingTop: 4,
          boxShadow: 'none !important',
        },
      }),
      menu: (props: any) => ({
        ...props,
        zIndex: 6,
      }),
      menuList: (props: any) => ({
        ...props,
        paddingTop: 0,
        paddingBottom: 0,
        fontSize: small ? '.875rem' : '1rem',
      }),
      placeholder: (props: any) => ({
        ...props,
        fontSize: small ? '.875rem' : '1rem',
      }),
    },
    components: { DropdownIndicator },
    theme: (theme: any) => ({
      ...theme,
      colors: {
        ...theme.colors,
        primary: '#57ae55',
        primary25: 'rgba(0, 0, 0, .05)',
        primary50: 'rgba(0, 0, 0, .1)',
      },
    }),
    isMulti: props.isMulti,
  };

  const error = fieldState.error;

  React.useEffect(() => {
    if (!wrapRef.current) return;

    if (
      inputValue ||
      (Array.isArray(field.value) && field.value.length) ||
      (!Array.isArray(field.value) && field.value)
    ) {
      wrapRef.current.classList.add('fl-is-active');
    } else {
      wrapRef.current.classList.remove('fl-is-active');
    }
  }, [wrapRef, inputValue, field.value]);

  return (
    <div
      className={classNames(
        'c-form-element',
        'c-form-element--select',
        'c-form-element--react-select',
        {
          [`c-form-element--style-${elStyle}`]: elStyle,
          'c-form-element--small': small,
          'c-form-element--error': typeof error !== 'undefined',
        },
        className
      )}
    >
      {typeof label !== 'undefined' ? (
        hasFloatingLabels ? (
          <div className="c-form-element__field">
            <div className="fl-wrap fl-wrap-input" ref={wrapRef}>
              <label htmlFor={name} className={classNames('c-form-label', { 'c-filters__title': isFiltersForm })}>
                {label}
              </label>
              <Select {...inputProps} />
            </div>
          </div>
        ) : (
          <>
            <label htmlFor={name} className={classNames('c-form-label', { 'c-filters__title': isFiltersForm })}>
              {label}
            </label>
            <div className="c-form-element__field">
              <Select {...inputProps} />
            </div>
          </>
        )
      ) : (
        <div className="c-form-element__field">
          <Select {...inputProps} />
        </div>
      )}
      <FieldError error={error} />
      {typeof helpText !== 'undefined' && <p className="c-note">{helpText}</p>}
    </div>
  );
};
